import { useCallback, useEffect, useMemo, useSyncExternalStore } from 'react';
import { CUSTOMER_ENTITY, USER_ENTITY } from 'Types/ReportField.types';
import { TreeNode } from 'Components/Primitives';
import { useCustomersTreeNodes } from 'Components/Customers';
import { useUsersTreeNodes } from 'Components/Users';
import { useIsFeatureFlagEnabled } from 'hooks';
import { FeatureFlag } from 'Constants/FeatureFlag.enum';
import { EntityAndReferenceData, TreeDataState } from 'Views/Reports/CustomReport/ReportEntity/ReportEntity.types';
import { useEntityRefValuesFromEntityMetadata } from 'Views/Reports/CustomReport/ReportEntity/hooks/useEntityRefValuesFromEntityMetadata';

interface UseEntityTreeNodesResponse {
  useDataForEntity: (
    e: EntityAndReferenceData,
    options?: { forceRefetch?: boolean; autoFetch?: boolean }
  ) => TreeDataState;
  triggerFetchForEntity: (e: EntityAndReferenceData, options?: { forceRefetch?: boolean }) => Promise<void>;
}

const useStoreSingleton = () => {
  return useMemo(
    () => ({
      data: new Map<string, TreeDataState>(),
      activeRequests: new Map<string, Promise<TreeNode[]>>(),
      listeners: new Map<string, Set<() => void>>(),

      subscribe(key: string, callback: () => void): () => void {
        if (!this.listeners.has(key)) this.listeners.set(key, new Set());
        this.listeners.get(key)!.add(callback);
        return () => {
          const keyListeners = this.listeners.get(key);
          if (keyListeners) {
            keyListeners.delete(callback);
            if (keyListeners.size === 0) this.listeners.delete(key);
          }
        };
      },

      getSnapshot(key: string): TreeDataState {
        return this.data.get(key) || { isLoading: false, error: null, data: undefined };
      },

      notifyListeners(key: string): void {
        this.listeners.get(key)?.forEach((listener) => listener());
      },

      // Methods to update the store from the hook
      setLoading(key: string): void {
        const current = this.getSnapshot(key);
        this.data.set(key, {
          ...current,
          isLoading: true,
          error: null,
          data: current.data
        }); // Keep data if refetching
        this.notifyListeners(key);
      },

      setData(key: string, fetchedData: TreeNode[]): void {
        this.data.set(key, { data: fetchedData, isLoading: false, error: null });
        this.notifyListeners(key);
      },

      setError(key: string, error: Error): void {
        const current = this.getSnapshot(key);
        this.data.set(key, {
          ...current,
          data: undefined,
          isLoading: false,
          error
        }); // Clear data on error
        this.notifyListeners(key);
      }
    }),
    []
  );
};

export const useEntityRefTreeNodes = (): UseEntityTreeNodesResponse => {
  const storeSingleton = useStoreSingleton();

  const { getCustomersTreeNodes } = useCustomersTreeNodes();
  const { getUserTreeNodes } = useUsersTreeNodes();
  const { getEntityRefValuesFromEntityMetadata } = useEntityRefValuesFromEntityMetadata();

  const isPartialListFetchEnabled = useIsFeatureFlagEnabled(FeatureFlag.PartialUserRefLists);

  const fetchTreeDataByKey = useCallback(
    async (e: EntityAndReferenceData): Promise<TreeNode[]> => {
      const { entity } = e;
      if (entity === USER_ENTITY) {
        // as long as the ff is not enabled we will always fetch the full list
        return getUserTreeNodes(
          isPartialListFetchEnabled && e.conditions ? { key: e.key, conditions: e.conditions } : null
        );
      } else if (entity === CUSTOMER_ENTITY) {
        return getCustomersTreeNodes();
      } else {
        return getEntityRefValuesFromEntityMetadata(entity);
      }
    },
    [isPartialListFetchEnabled, getCustomersTreeNodes, getUserTreeNodes, getEntityRefValuesFromEntityMetadata]
  );

  const fetchDataForHook = useCallback(
    async (e: EntityAndReferenceData, options?: { forceRefetch?: boolean }): Promise<void> => {
      const key = e.key;

      const currentDataState = storeSingleton.getSnapshot(key);

      // Prevent duplicate fetches if already in flight globally
      if (storeSingleton.activeRequests.has(key) && !options?.forceRefetch) {
        // Ensure the local component sees loading if it just subscribed
        if (!currentDataState.isLoading) storeSingleton.setLoading(key);
        return;
      }

      // Don't fetch if data exists and not forcing refetch
      if (currentDataState.data && !options?.forceRefetch && !currentDataState.isLoading) {
        return;
      }

      storeSingleton.setLoading(key); // Update store to loading

      try {
        const fetchPromise = fetchTreeDataByKey(e);
        storeSingleton.activeRequests.set(key, fetchPromise); // Track globally
        const fetchedNodes = await fetchPromise;
        storeSingleton.setData(key, fetchedNodes);
      } catch (err: unknown) {
        const error = err instanceof Error ? err : new Error('Unknown fetch error from hook');
        storeSingleton.setError(key, error);
      } finally {
        storeSingleton.activeRequests.delete(key); // Remove from global tracking
      }
    },
    [storeSingleton, fetchTreeDataByKey]
  );

  const useDataForKey = useCallback(
    (
      e: EntityAndReferenceData,
      options: { forceRefetch?: boolean; autoFetch?: boolean } = {
        autoFetch: true
      }
    ): TreeDataState => {
      const key = e.key;

      // Effect to trigger fetch on mount or if key/options change, controlled by autoFetch
      useEffect(() => {
        if (options.autoFetch) {
          fetchDataForHook(e, { forceRefetch: options.forceRefetch });
        }
      }, [e, options.forceRefetch, options.autoFetch]);

      // Subscribe to the store for reactive updates
      return useSyncExternalStore(
        (onStoreChange) => storeSingleton.subscribe(key, onStoreChange),
        () => storeSingleton.getSnapshot(key),
        () => storeSingleton.getSnapshot(key)
      );
    },
    [storeSingleton, fetchDataForHook]
  );

  return {
    useDataForEntity: useDataForKey,
    triggerFetchForEntity: fetchDataForHook // Expose the fetcher directly
  };
};
